import { memo, type ReactNode } from "react";
import { Flex } from "../flex";
import { SettingsRowLabel, type SettingsRowBadge } from "./settings-row-label";
import * as css from "./settings-rows.module.css";

/** Narrow control in the 196px column. A switch belongs on SettingsToggleRow. */
export const SettingsControlRow = memo(function SettingsControlRow({
  title,
  description,
  badge,
  controlWidth = "standard",
  trailing,
  children,
}: {
  title: string;
  description?: string;
  badge?: SettingsRowBadge;
  controlWidth?: "standard" | "content";
  /** Beside the control column, such as a row's ⋯ menu. */
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const control = (
    <ControlColumn controlWidth={controlWidth} fixed={!!trailing}>
      {children}
    </ControlColumn>
  );
  return (
    <Flex
      align="center"
      gap="3xl"
      justify="space-between"
      px="2xl"
      py="xl"
      w="full"
    >
      <SettingsRowLabel badge={badge} description={description} title={title} />
      {trailing ? (
        <Flex align="center" gap="sm" shrink={0}>
          {control}
          {trailing}
        </Flex>
      ) : (
        control
      )}
    </Flex>
  );
});

const ControlColumn = memo(function ControlColumn({
  controlWidth,
  fixed,
  children,
}: {
  controlWidth: "standard" | "content";
  fixed: boolean;
  children: ReactNode;
}) {
  return (
    <Flex
      align="flex-end"
      className={controlColumnClass(controlWidth, fixed)}
      dir="column"
      gap="none"
      shrink={0}
    >
      {children}
    </Flex>
  );
});

function controlColumnClass(
  controlWidth: "standard" | "content",
  fixed: boolean,
): string | undefined {
  if (controlWidth === "content") {
    return undefined;
  }
  return fixed ? css.fixedControlColumn : css.controlColumn;
}
